import { computed, toValue } from 'vue'
import { useHead } from '#imports'
import { useRoute } from 'vue-router'
import { siteUrl } from '@/config/site'

const SITE_NAME = 'FRISKY'
const CANONICAL_ORIGIN = 'https://frisky.fm'

// The card image for any view that has no image of its own to share.
export function socialShareImage(): string {
    return siteUrl('/frisky-social-share-image.png')
}

// Every view describes its head the same way: a title and a description, and the
// shareable ones add an image. Passing an `image` is what turns on the Open Graph /
// Twitter card set and the canonical link — that is the split the views already had.
//
// `key` is what vue-meta called `vmid`: it is what stops a view's tag and the global
// one in `app.vue` rendering twice.
export function usePageHead(source) {
    const route = useRoute()

    useHead(computed(() => {
        const { title, description, image, type = 'website', canonical } = toValue(source) || {}

        // A tag with nothing in it is worse than no tag: a view whose data is still
        // loading would publish an empty description to whatever is scraping it.
        const meta = [
            { name: 'title', key: 'title', content: title },
            { name: 'description', key: 'description', content: description },

            { property: 'og:title', key: 'og:title', content: title },
            { property: 'og:description', key: 'og:description', content: description },

            { name: 'twitter:title', key: 'twitter:title', content: title },
            { name: 'twitter:description', key: 'twitter:description', content: description },
        ].filter(tag => tag.content)

        if (!image) return { title, meta }

        // Only a shareable view needs a URL, and only that view is ever mounted
        // inside a router — `useRoute()` is undefined without one.
        const url = canonical || `${CANONICAL_ORIGIN}${route?.path || ''}`

        return {
            title,
            link: [{ rel: 'canonical', key: 'canonical', href: url }],
            meta: [
                ...meta,
                { property: 'og:image', key: 'og:image', content: image },
                { property: 'og:type', key: 'og:type', content: type },
                { property: 'og:url', key: 'og:url', content: url },
                { property: 'og:site_name', key: 'og:site_name', content: SITE_NAME },

                { name: 'twitter:card', key: 'twitter:card', content: 'summary_large_image' },
                { name: 'twitter:image', key: 'twitter:image', content: image },
                { name: 'twitter:url', key: 'twitter:url', content: url },
            ],
        }
    }))
}
